import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import {
  Users,
  FolderKanban,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  Plus,
  FileText
} from 'lucide-react';
import UserSettingsModal from './UserSettingsModal';
import { CreateProjectModal } from './CreateProjectModal';
import { createProject, logout as apiLogout } from '../services/api';
import Logo from './ui/Logo';
import { useUser } from '../contexts/UserContext';

interface LayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, currentPage = 'projects' }) => {
  const navigate = useNavigate();
  const { logout } = useUser();
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  const navigation = [
    { id: 'projects', label: 'Projetos', icon: FolderKanban, path: '/projects' },
    { id: 'calendar', label: 'Calendário', icon: Calendar, path: '/calendar' },
    { id: 'team', label: 'Equipe', icon: Users, path: '/team' },
    { id: 'reports', label: 'Relatórios', icon: FileText, path: '/reports' },
    { id: 'analytics', label: 'Análises', icon: BarChart3, path: '/analytics' },
  ] as const;

  const handleLogout = async () => {
    try {
      // Call API logout first to invalidate refresh token
      await apiLogout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with client-side logout even if API fails
    } finally {
      // Always clear local state
      logout();
      navigate('/login');
    }
  };

  const handleCreateProject = async (projectData: {
    name: string;
    description?: string;
    jiraEnabled?: boolean;
    jiraConfig?: any;
  }) => {
    try {
      const response = await createProject(projectData);
      const newProject = response.data.data.project;
      
      // Close modal
      setIsCreateProjectModalOpen(false);
      
      // Navigate to projects page and then to the new project
      navigate('/projects');
      
      // Optional: Navigate directly to the new project
      // setTimeout(() => {
      //   navigate(`/projects/${newProject._id}`);
      // }, 100);
    } catch (error) {
      console.error('Error creating project:', error);
      // The modal will handle error display
      throw error;
    }
  };

  return (
    <div className="h-screen flex bg-background-primary">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0f1419] border-r border-white/5">
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6">
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <div>
                <h1 className="text-white font-semibold font-heading">ProjectHub</h1>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2">
            <div className="mb-6">
              <p className="text-gray-600 text-xs font-medium uppercase tracking-wider px-3 mb-2">Navegação</p>
              <ul className="space-y-0.5">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.id;
                  
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => navigate(item.path)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded group',
                          'hover:bg-white/5',
                          isActive ? 'bg-blue-500/10 text-blue-400' : 'text-gray-400'
                        )}
                      >
                        <Icon className={cn(
                          "w-5 h-5",
                          isActive ? "text-blue-400" : "text-gray-500 group-hover:text-gray-400"
                        )} />
                        <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            
            <div>
              <p className="text-gray-600 text-xs font-medium uppercase tracking-wider px-3 mb-2">Ações Rápidas</p>
              <ul className="space-y-0.5">
                <li>
                  <button 
                    onClick={() => setIsCreateProjectModalOpen(true)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-gray-400 hover:bg-white/5 hover:text-gray-300"
                  >
                    <Plus className="w-5 h-5 text-gray-500" />
                    <span className="flex-1 text-left text-sm font-medium">Novo Projeto</span>
                  </button>
                </li>
              </ul>
            </div>
          </nav>

          {/* Bottom Actions */}
          <div className="p-4">
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsUserSettingsOpen(true)}
                className="p-2 hover:bg-white/5 rounded group"
              >
                <Settings className="w-5 h-5 text-gray-500 group-hover:text-gray-400" />
              </button>
              <button 
                onClick={handleLogout}
                className="p-2 hover:bg-red-500/10 rounded group"
              >
                <LogOut className="w-5 h-5 text-gray-500 group-hover:text-red-400" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 flex flex-col bg-[#0a0c10]">
        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {children}
        </div>
      </main>

      {/* User Settings Modal */}
      <UserSettingsModal
        isOpen={isUserSettingsOpen}
        onClose={() => setIsUserSettingsOpen(false)}
      />

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
        onCreateProject={handleCreateProject}
      />
    </div>
  );
};